import React, { useState } from "react";
import {
  TextField,
  MenuItem,
  Box,
  Typography,
  FormHelperText,
  Grid,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const generateSixDigitNumber = () => {
  return Math.floor(100000 + Math.random() * 900000);
};

const Step3GenerateTag = ({
  sectionTitleSx,
  sectionSubTextSx,
  secondaryButtonSx,
  primaryButtonSx,
  handleBack,
  handleNext,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    dsp: "",
    seatId: "",
    dealName: "",
    dealFormat: "",
    margin: "",
    cpm: "",
    agencyCode: "",
    phone: "",
    email: "",
    randNumber: generateSixDigitNumber(),
  });

  const [csvFile, setCsvFile] = useState(null);
  const [parsedUrls, setParsedUrls] = useState([]);
  const [errors, setErrors] = useState({});
  const [csvError, setCsvError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.dsp) newErrors.dsp = "DSP is required";
    if (!formData.dealName) newErrors.dealName = "Deal Name is required";
    if (!formData.dealFormat) newErrors.dealFormat = "Deal Format is required";
    if (!formData.margin) newErrors.margin = "Curator Margin is required";
    if (!formData.cpm) newErrors.cpm = "CPM is required";
    if (!formData.agencyCode) newErrors.agencyCode = "Agency Code is required";
    if (!formData.phone) newErrors.phone = "Phone is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!parsedUrls.length) newErrors.csv = "CSV file is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const downloadCsvTemplate = () => {
    const csvContent = "url\nhttps://example.com\nhttps://example2.com\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "url_list_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ✅ SUCCESS UI
  if (isSubmitted) {
    return (
      <Box sx={{ textAlign: "center", p: 4 }}>
        <CheckCircleIcon sx={{ color: "green", fontSize: 60, mb: 2 }} />
        <Typography variant="h5">
          Tag Generated ({formData.dealName})
        </Typography>
        {/* <Typography sx={{ mt: 1 }}>
          This is a demo submission. No API was called.
        </Typography> */}

        <Typography sx={{ mt: 2 }}>
          Email: <strong>{formData.email}</strong>
        </Typography>

        <Typography sx={{ mt: 2 }} variant="h6">
          Reference Code: {formData.randNumber}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
            sx={secondaryButtonSx}
          >
            Regenerate
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Typography variant="h5" sx={sectionTitleSx}>
        Generate Tag
      </Typography>

      <Typography sx={sectionSubTextSx}>
        Fill details to complete your request
      </Typography>

      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Select DSP"
              name="dsp"
              value={formData.dsp}
              onChange={handleChange}
              error={!!errors.dsp}
              helperText={errors.dsp}
            >
              <MenuItem value="1">DV360</MenuItem>
              <MenuItem value="2">TTD</MenuItem>
              <MenuItem value="3">Amazon DSP</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Seat ID"
              name="seatId"
              value={formData.seatId}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Deal Name"
              name="dealName"
              value={formData.dealName}
              onChange={handleChange}
              error={!!errors.dealName}
              helperText={errors.dealName}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Deal Format"
              name="dealFormat"
              value={formData.dealFormat}
              onChange={handleChange}
              error={!!errors.dealFormat}
              helperText={errors.dealFormat}
            >
              <MenuItem value="1">Display</MenuItem>
              <MenuItem value="2">Video</MenuItem>
              <MenuItem value="3">CTV</MenuItem>
              <MenuItem value="4">All</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Curator Margin %"
              type="number"
              name="margin"
              value={formData.margin}
              onChange={handleChange}
              error={!!errors.margin}
              helperText={errors.margin}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Buying CPM"
              type="number"
              name="cpm"
              value={formData.cpm}
              onChange={handleChange}
              error={!!errors.cpm}
              helperText={errors.cpm}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Agency Code"
              name="agencyCode"
              value={formData.agencyCode}
              onChange={handleChange}
              error={!!errors.agencyCode}
              helperText={errors.agencyCode}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body2">Upload URL List (CSV only)</Typography>

            <input
              type="file"
              accept=".csv"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;

                if (!file.name.endsWith(".csv")) {
                  setCsvError("Only CSV files are allowed");
                  return;
                }

                const reader = new FileReader();

                reader.onload = (event) => {
                  const lines = event.target.result.split(/\r?\n/);
                  const urls = lines
                    .slice(1)
                    .map((l) => l.trim())
                    .filter(Boolean);

                  setParsedUrls(urls);
                  setCsvFile(file);
                  setCsvError("");
                };

                reader.readAsText(file);
              }}
            />

            {csvFile && (
              <FormHelperText sx={{ color: "green" }}>
                {csvFile.name} ({parsedUrls.length} URLs parsed)
              </FormHelperText>
            )}

            {(errors.csv || csvError) && (
              <FormHelperText error>{errors.csv || csvError}</FormHelperText>
            )}

            <Button size="small" sx={{ mt: 1 }} onClick={downloadCsvTemplate}>
              Download CSV Template
            </Button>
          </Grid>
        </Grid>

        {/* ✅ CONSISTENT BUTTONS */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 5,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 3,
              py: 1.2,
              fontWeight: 500,
              ...secondaryButtonSx, // keep this last
            }}
          >
            Previous
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              if (validate()) {
                handleNext();
                setIsSubmitted(true);
              }
            }}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 3,
              py: 1.2,
              fontWeight: 500,
              boxShadow: "none",
              ...primaryButtonSx, // keep this last
            }}
          >
            Generate Tag
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default Step3GenerateTag;
